import React, { Component } from 'react';
import yaml from 'js-yaml';

import { buildPath } from '../utils/route_utils';
import { trackEvent } from '../utils/analytics';
import { observeRise } from '../utils/reveal';

import '../css/LatestNewsSection.css';

/**
 * LatestNewsSection — the last band on the home page before the footer.
 * Reads /yaml/blog_posts.yaml at runtime, keeps the three newest posts by
 * `date`, and renders them as three edge-to-edge photo tiles (the post
 * image under a bottom-up scrim; "category · date", headline and read
 * link overlaid at the bottom-left). Renders nothing until the data is in
 * and nothing at all if the fetch fails, so the page never shows a
 * skeleton.
 *
 * Props: lang ('zh' | 'en' | 'ja'), navigate(route).
 */

const LANGS = ['zh', 'en', 'ja'];
const POST_COUNT = 3;
const STAGGER_MS = 60;
const BLOG_ROUTE = '/blog';

const COPY = {
  title: { zh: 'Nexuni 最新動態', en: 'Latest from Nexuni', ja: 'Nexuni の最新情報' },
  all:   { zh: '所有消息', en: 'All news', ja: 'すべてのニュース' },
  read:  { zh: '閱讀', en: 'Read', ja: '読む' }
};

/** Tri-language value -> string for `lang`, falling back to en, then ''. */
function pick(value, lang) {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  const own = value[lang];
  if (typeof own === 'string' && own.trim()) return own;
  return typeof value.en === 'string' ? value.en : '';
}

/** Normalise a YAML date (quoted ISO string, or a Date if unquoted) to 'YYYY-MM-DD'. */
function isoDate(value) {
  if (value instanceof Date && !isNaN(value.getTime())) return value.toISOString().slice(0, 10);
  if (typeof value === 'string') return value.trim().slice(0, 10);
  return '';
}

/** 'YYYY-MM-DD' -> 'YYYY.MM.DD' */
function displayDate(iso) {
  return iso.replace(/-/g, '.');
}

/** Resolve a yaml image path against PUBLIC_URL; leave absolute URLs alone. */
function assetUrl(src) {
  if (typeof src !== 'string' || !src) return '';
  if (/^(https?:)?\/\//i.test(src)) return src;
  return process.env.PUBLIC_URL + (src.charAt(0) === '/' ? src : '/' + src);
}

/**
 * Where a tile goes. Posts with a dedicated article route carry `link`
 * (e.g. '/blog/laundry-gr00t'); every other post expands in place on the
 * blog index, which expands the post addressed by the URL hash.
 */
function postRoute(post) {
  const link = post && typeof post.link === 'string' ? post.link.trim() : '';
  if (link) return link;
  // BlogPage expands and scrolls to '#<post id>' once its posts have loaded.
  return post && post.id ? BLOG_ROUTE + '#' + post.id : BLOG_ROUTE;
}

class LatestNewsSection extends Component {
  constructor(props) {
    super(props);
    this.state = { posts: [] };
    this.rootRef = React.createRef();
    this.mounted = false;
    this.disposeRise = null;
    this.handleAllClick = this.handleAllClick.bind(this);
  }

  componentDidMount() {
    this.mounted = true;
    fetch(process.env.PUBLIC_URL + '/yaml/blog_posts.yaml')
      .then(res => (res.ok ? res.text() : Promise.reject(new Error('HTTP ' + res.status))))
      .then(text => {
        const parsed = yaml.load(text);
        const all = parsed && Array.isArray(parsed.posts) ? parsed.posts : [];
        const latest = all
          .filter(p => p && p.id && p.title)
          .map(p => ({ ...p, iso: isoDate(p.date) }))
          .sort((a, b) => (a.iso < b.iso ? 1 : a.iso > b.iso ? -1 : 0))
          .slice(0, POST_COUNT);
        if (this.mounted && latest.length > 0) {
          // The .rise nodes only exist once the tiles are in the DOM.
          this.setState({ posts: latest }, () => {
            this.disposeRise = observeRise(this.rootRef.current);
          });
        }
      })
      .catch(() => { /* optional section: stay empty */ });
  }

  componentWillUnmount() {
    this.mounted = false;
    if (this.disposeRise) this.disposeRise();
  }

  /**
   * Plain left-clicks go through the page's navigate(); clicks with a
   * modifier key keep the browser's native behaviour (new tab, etc.) via
   * the real href.
   */
  go(e, route, eventName, params) {
    trackEvent(eventName, params);
    if (typeof this.props.navigate !== 'function') return;
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    this.props.navigate(route);
  }

  handleAllClick(e) {
    this.go(e, BLOG_ROUTE, 'view_all_news', { location: 'home_news' });
  }

  handleCardClick(e, post, route, index) {
    this.go(e, route, 'select_news', {
      location: 'home_news',
      post_id: post.id,
      position: index + 1,
      destination: route
    });
  }

  render() {
    const posts = this.state.posts;
    if (posts.length === 0) return null;

    const lang = LANGS.indexOf(this.props.lang) !== -1 ? this.props.lang : 'en';

    return (
      <section className="news-section" ref={this.rootRef} aria-labelledby="news-section-title">

        <div className="news-head rise">
          <h2 className="news-title h-display" id="news-section-title">{COPY.title[lang]}</h2>
          <a className="btn-link news-head__all" href={buildPath(lang, BLOG_ROUTE)} onClick={this.handleAllClick}>
            {COPY.all[lang]}<span className="arrow" aria-hidden="true">→</span>
          </a>
        </div>

        <ul className="news-grid" role="list">
          {posts.map((post, index) => {
            const route = postRoute(post);
            const title = pick(post.title, lang);
            const category = pick(post.category, lang);
            const image = assetUrl(post.image);
            return (
              <li className="news-grid__item" key={post.id}>
                <a
                  className="news-card rise"
                  href={buildPath(lang, route)}
                  onClick={(e) => this.handleCardClick(e, post, route, index)}
                  style={{ transitionDelay: (index * STAGGER_MS) + 'ms' }}
                >
                  <div className="news-card__media">
                    {image && (
                      // Decorative inside the link: the headline is the link text.
                      <img
                        src={image}
                        alt=""
                        loading="lazy"
                        decoding="async"
                        style={{ objectPosition: post.image_position || 'center' }}
                      />
                    )}
                  </div>
                  <span className="news-card__scrim" aria-hidden="true"></span>
                  <div className="news-card__body">
                    <div className="news-card__meta">
                      {category && <span className="news-card__category">{category}</span>}
                      {category && post.iso && <span className="news-card__sep" aria-hidden="true">·</span>}
                      {post.iso && <time className="news-card__date" dateTime={post.iso}>{displayDate(post.iso)}</time>}
                    </div>
                    <h3 className="news-card__title">{title}</h3>
                    <span className="news-card__read">
                      {COPY.read[lang]}<span className="arrow" aria-hidden="true">→</span>
                    </span>
                  </div>
                </a>
              </li>
            );
          })}
        </ul>

      </section>
    );
  }
}

export default LatestNewsSection;
